import { forwardRef, useEffect, useRef } from 'react';
import { ReactRenderer } from '@tiptap/react';

import VAvatar from '../VAvatar.jsx';

const listStyle = {
  minWidth: '12rem',
  maxHeight: '15rem',
  overflowY: 'auto',
  background: 'var(--chakra-colors-bg-panel, #ffffff)',
  border: '1px solid var(--chakra-colors-border, #e2e8f0)',
  borderRadius: 'var(--chakra-radii-l2, 8px)',
  boxShadow: 'var(--chakra-shadows-lg, 0 6px 16px rgba(0, 0, 0, 0.12))',
  padding: '4px 0',
};

const itemStyle = (selected) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  cursor: 'pointer',
  padding: '6px 12px',
  background: selected ? 'var(--chakra-colors-bg-muted, #f1f5f9)' : 'transparent',
});

const textColumnStyle = { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 };
const truncated = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const nameStyle = {
  ...truncated,
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--chakra-colors-fg, #0f172a)',
};
const emailStyle = {
  ...truncated,
  fontSize: '12px',
  color: 'var(--chakra-colors-fg-muted, #64748b)',
};

export const MentionList = forwardRef(function MentionList(
  { items, selectedIndex, onSelect },
  ref
) {
  const selectedRef = useRef(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div ref={ref} role="listbox" aria-label="User mentions" style={listStyle}>
      {items.map((item, index) => (
        <div
          key={item.id}
          ref={index === selectedIndex ? selectedRef : undefined}
          role="option"
          aria-selected={index === selectedIndex}
          tabIndex={-1}
          style={itemStyle(index === selectedIndex)}
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(item);
          }}
        >
          <VAvatar src={item.avatar} name={item.value} size="xs" />

          <span style={textColumnStyle}>
            <span style={nameStyle}>{item.value}</span>
            {item.email && <span style={emailStyle}>{item.email}</span>}
          </span>
        </div>
      ))}
    </div>
  );
});

export const createMentionSuggestion = (onSearch, container) => ({
  char: '@',
  container: container ?? undefined,
  dismissOnOutsideClick: false,
  minQueryLength: 3,
  debounce: 300,
  items: ({ query }) => (onSearch ? onSearch(query) : []),
  render: () => {
    const state = { selectedIndex: 0 };

    const buildProps = (props) => ({
      items: props.items,
      selectedIndex: state.selectedIndex,
      onSelect: (user) => props.command({ id: user.id, label: user.value }),
    });

    const highlight = (index) => {
      state.selectedIndex = index;

      if (state.component) {
        state.component.updateProps({ ...state.component.props, selectedIndex: index });
      }
    };

    return {
      onStart: (props) => {
        state.selectedIndex = 0;
        state.component = new ReactRenderer(MentionList, {
          props: buildProps(props),
          editor: props.editor,
        });

        if (props.clientRect) {
          state.unmount = props.mount(state.component.element);
        }
      },
      onUpdate: (props) => {
        state.selectedIndex = Math.min(state.selectedIndex, Math.max(props.items.length - 1, 0));
        state.component?.updateProps(buildProps(props));
      },
      onKeyDown: (props) => {
        const items = state.component?.props.items ?? [];

        if (items.length === 0) {
          return false;
        }

        if (props.event.key === 'ArrowUp') {
          highlight((state.selectedIndex - 1 + items.length) % items.length);
          return true;
        }

        if (props.event.key === 'ArrowDown') {
          highlight((state.selectedIndex + 1) % items.length);
          return true;
        }

        if (props.event.key === 'Enter') {
          const item = items[state.selectedIndex];

          if (item) {
            state.component?.props.onSelect(item);
          }

          return true;
        }

        if (props.event.key === 'Escape') {
          state.unmount?.();
          return true;
        }

        return false;
      },
      onExit: () => {
        state.unmount?.();
        state.component?.destroy();
      },
    };
  },
});
